import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import tyroConfigService from '../services/tyroConfigService';
import tyroReportsService from '../services/tyroReportsService';
import storeService from '../services/storeService';
import { sanitizeTyroTextReport, parseTyroXmlReport } from '../utils/tyroReportUtils';
import { getTyroClient } from '../utils/tyroClient';
import { formatInStoreTimezone, toApiDateValue } from '../utils/dateUtils';
import useStoreTimezone from '../hooks/useStoreTimezone';

const REPORT_TYPES = [
  { label: 'Summary', value: 'summary' },
  { label: 'Detailed', value: 'detail' },
];

const REPORT_FORMATS = [
  { label: 'Text', value: 'txt' },
  { label: 'XML', value: 'xml' },
];

const TyroReportsPage = () => {
  const { activeStore } = useStore();
  const { tillId, getCurrentTillForStore } = useAuth();
  const currentTillId = activeStore?._id ? getCurrentTillForStore(activeStore._id) : tillId;
  const timezone = useStoreTimezone();
  const [tyro, setTyro] = useState(null);
  const [tyroConfig, setTyroConfig] = useState(null);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [formDate, setFormDate] = useState('');
  const [reportType, setReportType] = useState('summary');
  const [format, setFormat] = useState('txt');
  const [statusMessages, setStatusMessages] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [reportPreview, setReportPreview] = useState('');
  const [reportXml, setReportXml] = useState('');
  const [parsedXml, setParsedXml] = useState(null);
  const [error, setError] = useState('');
  const [tyroPrompt, setTyroPrompt] = useState(null);
  const [recentReports, setRecentReports] = useState([]);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return toApiDateValue(d);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const initTyro = async () => {
      if (!activeStore) return;
      setIsLoadingConfig(true);
      try {
        const config = await tyroConfigService.getTyroConfig(activeStore._id, { includeSensitive: true });
        // Reports should use the active till's pairing details, not store-level keys.
        let resolvedConfig = { ...config, tid: null, integrationKey: null, tillName: null };
        if (currentTillId) {
          try {
            const storeDetails = await storeService.getStoreById(activeStore._id);
            const matchingTill = storeDetails?.tills?.find((till) => till._id === currentTillId);
            resolvedConfig = {
              ...resolvedConfig,
              tid: matchingTill?.tid || null,
              integrationKey: matchingTill?.tyroIntegrationKey || null,
              tillName: matchingTill?.name || null,
            };
          } catch (tillError) {
            console.warn('Failed to resolve till-level Tyro settings', tillError);
          }
        }
        if (!isMounted) return;
        setTyroConfig(resolvedConfig);
        if (resolvedConfig?.apiKey) {
          const siteReference =
            [activeStore?.address?.street, activeStore?.address?.suburb, activeStore?.address?.state]
              .filter(Boolean)
              .join(', ') ||
            activeStore?.name ||
            '';
          const posProductInfo = {
            posProductVendor: 'Bizessence Pty Ltd',
            posProductName: 'BoltinPOS',
            posProductVersion: '1.0.0',
            siteReference,
          };
          try {
            const localHost = String(window?.location?.hostname || '').toLowerCase();
            const forceHeadlessForLocalhost = ['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]'].includes(localHost);
            const preferHeadless = forceHeadlessForLocalhost ? true : resolvedConfig.useHeadlessMode !== false;
            const iClient = await getTyroClient({
              apiKey: resolvedConfig.apiKey,
              posProductInfo,
              preferHeadless,
            });
            if (isMounted) setTyro(iClient);
          } catch (clientError) {
            console.error('Failed to initialize Tyro client', clientError);
            if (isMounted) setTyro(null);
            setError(clientError.message || 'Failed to load Tyro SDK.');
            return;
          }
        } else {
          setError('Tyro configuration is incomplete for this store.');
        }
      } catch (err) {
        console.error('Failed to load Tyro config', err);
        if (!isMounted) return;
        if (err?.response?.status === 404) {
          setTyroConfig(null);
          setError('Tyro configuration not found for this store. Please save the MID/TID in Store Settings → Tyro before requesting reports.');
        } else {
          setError('Failed to load Tyro configuration for this store.');
        }
      } finally {
        if (isMounted) setIsLoadingConfig(false);
      }
    };

    initTyro();

    return () => {
      isMounted = false;
    };
  }, [activeStore, currentTillId]);

  useEffect(() => {
    const loadReports = async () => {
      if (!activeStore) return;
      try {
        const reports = await tyroReportsService.fetchTyroReports(activeStore._id);
        setRecentReports(reports);
      } catch (err) {
        console.warn('Failed to load stored Tyro reports', err);
      }
    };

    loadReports();
  }, [activeStore]);

  const validateForm = () => {
    if (!formDate) {
      setError('Please select a date to request the report.');
      return false;
    }
    if (!tyroConfig?.mid) {
      setError('Tyro MID is required before requesting reports.');
      return false;
    }
    if (!currentTillId || !tyroConfig?.tid || !tyroConfig?.integrationKey) {
      setError('Select a terminal and pair it in Settings -> Tyro before requesting reports.');
      return false;
    }
    setError('');
    return true;
  };

  const logReportSafely = async (payload) => {
    try {
      await tyroReportsService.logTyroReport(payload);
    } catch (logErr) {
      console.warn('Failed to store Tyro report', logErr);
    }
  };

  const handleGenerateReport = async () => {
    if (!tyro || !validateForm()) return;
    setProcessing(true);
    setStatusMessages(['Connecting to Tyro terminal...']);
    setReportResult(null);
    setReportPreview('');
    setReportXml('');
    setParsedXml(null);

    const terminalBusinessDay = new Date(formDate);
    const isoDate = terminalBusinessDay.toISOString().split('T')[0];
    const formattedDate = [
      terminalBusinessDay.getFullYear(),
      String(terminalBusinessDay.getMonth() + 1).padStart(2, '0'),
      String(terminalBusinessDay.getDate()).padStart(2, '0'),
    ].join('');
    const { mid, tid, integrationKey } = tyroConfig;
    if (!mid || !tid || !integrationKey) {
      setProcessing(false);
      setError('Tyro MID/TID and a paired terminal are required before requesting reports.');
      return;
    }

    const requestParams = {
      mid: parseInt(mid, 10),
      tid: parseInt(tid, 10),
      integrationKey,
      terminalBusinessDay: formattedDate,
      type: reportType,
      format,
    };

    const questionCallback = (question, answerCallback) => {
      console.info('Tyro question received', question);
      if (typeof answerCallback !== 'function') return;
      const options = Array.isArray(question?.options) && question.options.length > 0
        ? question.options
        : Array.isArray(question?.buttons) && question.buttons.length > 0
          ? question.buttons
          : ['OK'];
      const promptText =
        question?.text ||
        question?.displayText ||
        question?.message ||
        question?.prompt ||
        question?.status ||
        'Please respond on the terminal.';
      setTyroPrompt({
        text: promptText,
        options,
        answerCallback,
        awaitingResult: false,
      });
    };


    const statusHandler = (message) => {
      const text = typeof message === 'string' ? message : JSON.stringify(message);
      setStatusMessages((prev) => [...prev, text]);

      if (typeof message === 'object' && message !== null && message.result) {
        // This looks like a final response, so call the responseHandler
        responseHandler(message);
      }
    };

    const responseHandler = async (response) => {
      setProcessing(false);
      const responseResult = response?.result?.toLowerCase();
      if (responseResult === 'failure') {
        const failureMessage = response?.error || response?.errorMessage || 'Report generation failed.';
        setError(failureMessage);
        setReportResult('failure');
        await logReportSafely({
          storeId: activeStore._id,
          terminalBusinessDay: isoDate,
          reportType,
          format,
          status: 'failure',
          errorMessage: failureMessage,
          rawPayload: response,
        });
        return;
      }

      setError('');
      setReportResult('success');
      const rawReport = response?.report || response?.reportContent || response?.textReport || response?.xmlReport || response?.data || '';

      if (format === 'txt') {
        const sanitized = sanitizeTyroTextReport(rawReport);
        setReportPreview(sanitized);
        await logReportSafely({
          storeId: activeStore._id,
          terminalBusinessDay: isoDate,
          reportType,
          format,
          status: 'success',
          reportText: sanitized,
          rawPayload: response,
        });
      } else {
        setReportXml(rawReport);
        const parsed = parseTyroXmlReport(rawReport);
        setParsedXml(parsed);
        await logReportSafely({
          storeId: activeStore._id,
          terminalBusinessDay: isoDate,
          reportType,
          format,
          status: 'success',
          parsedReport: parsed,
          rawPayload: response,
        });
      }
    };

    const errorHandler = async (error) => {
      const failureMessage = error?.message || 'Report request failed.';
      setProcessing(false);
      setReportResult('failure');
      setError(failureMessage);
      await logReportSafely({
        storeId: activeStore._id,
          terminalBusinessDay: isoDate,
        reportType,
        format,
        status: 'failure',
        errorMessage: failureMessage,
      });
    };

    const invokeReconciliationReport = async () => {
      try {
        // This is a speculative attempt based on the library's observed behavior.
        // The ideal signature would be known from documentation.
        tyro.reconciliationReport(requestParams, statusHandler, responseHandler, errorHandler, questionCallback);
      } catch (err) {
        // Fallback for older/different library versions
        if (err?.message?.includes('Callback parameter must be a function') || err?.message?.includes('arguments')) {
          try {
            tyro.reconciliationReport(requestParams, responseHandler, questionCallback);
          } catch (nestedError) {
            throw nestedError;
          }
        } else {
          throw err;
        }
      }
    };

    try {
      await invokeReconciliationReport();
    } catch (err) {
      console.error('Failed to initiate Tyro report', err);
      setProcessing(false);
      setError(err.message || 'Unable to initiate Tyro report.');
      await logReportSafely({
        storeId: activeStore._id,
          terminalBusinessDay: isoDate,
        reportType,
        format,
        status: 'failure',
        errorMessage: err.message || 'Unable to initiate Tyro report.',
      });
    }
  };

  // eslint-disable-next-line no-unused-vars -- UI for Tyro prompts will consume this handler soon
  const handleTyroPromptAnswer = (option) => {
    if (!tyroPrompt) return;
    try {
      tyroPrompt.answerCallback(option);
      setTyroPrompt((prev) => (prev ? { ...prev, awaitingResult: true, selectedOption: option } : null));
    } catch (err) {
      console.error('Failed to send Tyro answer', err);
      setTyroPrompt(null);
    }
  };

  const handlePrint = () => {
    const content = format === 'txt' ? reportPreview : reportXml;
    if (!content) return;
    const printWindow = window.open('', '_blank', 'width=600,height=800');
    printWindow.document.write(`<pre>${content}</pre>`);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  if (!activeStore) {
    return <div>Please select a store to request Tyro reports.</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Generate Tyro Reconciliation Report</h2>
        <p className="text-gray-600">Request summary or detailed reports for the past 7 business days.</p>
      </div>

      {error && <div className="bg-red-50 text-red-700 border border-red-200 rounded p-3">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Report date</label>
          <input
            type="date"
            min={minDate}
            max={toApiDateValue(new Date())}
            value={formDate}
            onChange={(e) => setFormDate(e.target.value)}
            className="w-full border-gray-300 rounded-md shadow-sm"
            disabled={isLoadingConfig}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Report type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="w-full border-gray-300 rounded-md shadow-sm"
          >
            {REPORT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="w-full border-gray-300 rounded-md shadow-sm"
          >
            {REPORT_FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        type="button"
        onClick={handleGenerateReport}
        disabled={!tyro || processing || !formDate}
        className="bg-primary text-white px-6 py-3 rounded-md font-semibold disabled:bg-gray-400"
      >
        {processing ? 'Requesting report...' : 'Generate Report'}
      </button>

      {statusMessages.length > 0 && (
        <div className="bg-gray-50 border rounded-md p-3 max-h-48 overflow-auto text-sm">
          {statusMessages.map((msg, idx) => (
            <div key={`${msg}-${idx}`} className="flex items-start">
              <span className="mr-2 text-gray-400">•</span>
              <span>{msg}</span>
            </div>
          ))}
        </div>
      )}

      {reportResult === 'success' && (
        <div className="bg-white border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Report Preview</h3>
            <div className="space-x-2">
              <button
                type="button"
                onClick={handlePrint}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Print
              </button>
              <a
                href={`data:text/plain;charset=utf-8,${encodeURIComponent(format === 'txt' ? reportPreview : reportXml)}`}
                download={`tyro-report-${formDate}.${format === 'txt' ? 'txt' : 'xml'}`}
                className="px-4 py-2 bg-secondary text-white rounded-md"
              >
                Download
              </a>
            </div>
          </div>

          {format === 'txt' && (
            <pre className="bg-gray-100 rounded-md p-3 text-sm max-h-96 overflow-auto whitespace-pre-wrap">{reportPreview}</pre>
          )}

          {format === 'xml' && (
            <div className="space-y-4">
              <pre className="bg-gray-100 rounded-md p-3 text-xs max-h-96 overflow-auto whitespace-pre-wrap">{reportXml}</pre>
              {parsedXml && (
                <div className="bg-blue-50 border border-blue-100 rounded-md p-3">
                  <h4 className="font-semibold mb-2">Parsed summary</h4>
                  <p>Total Amount: {parsedXml.summary.totalAmount || 'N/A'}</p>
                  <p>Total Count: {parsedXml.summary.totalCount || 'N/A'}</p>
                  {parsedXml.batches?.length > 0 && (
                    <div className="mt-3">
                      <p className="font-semibold">Batches</p>
                      <ul className="list-disc list-inside text-sm">
                        {parsedXml.batches.map((batch, index) => (
                          <li key={`${batch.cardType}-${index}`}>
                            {batch.cardType || 'Unknown'} — {batch.count || '-'} tx, {batch.amount || '-'}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bg-white border rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-3">Recent requests</h3>
        {recentReports.length === 0 && <p className="text-sm text-gray-500">No reports stored yet.</p>}
        {recentReports.length > 0 && (
          <div className="space-y-2">
            {recentReports.map((report) => (
              <div
                key={report._id}
                className="flex items-center justify-between border rounded-md p-3 text-sm"
              >
                <div>
                <p className="font-semibold">
                  {formatInStoreTimezone(report.createdAt, 'dd-MM-yyyy hh:mm a', timezone)}
                </p>
                  <p className="text-gray-600">
                    {report.reportType} / {report.format} / {report.terminalBusinessDay}
                  </p>
                </div>
                <span
                  className={`text-xs font-semibold px-2 py-1 rounded ${
                    report.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {report.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TyroReportsPage;
